"use client";

import { FC, useEffect } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import useWindowSize from "react-use/lib/useWindowSize";
import Confetti from "react-confetti";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { QuizResult, QuizType } from "@/types/quiz";
import { useAppSelector } from "@/redux/store";
import { useLocalStorage } from "@/hooks/useStorage";
import {
  getScorePercentage,
  formatTime,
} from "@/components/quiz/results/QuestionAnswerScorecard/helpers";
import QuizResultHeader from "@/components/quiz/results/QuizResultHeader";
import QuizResultThumbnail from "@/components/quiz/results/QuizResultThumbnail";
import Watermark from "@/components/common/Watermark";
import Divider from "@/components/common/Divider";
import QuizResultControls from "@/components/quiz/results/QuizResultControls";
import MonumetricAd from "@/components/adverts/MonumetricAd";
import LeaderboardWrapper from "@/components/quiz/leaderboard/LeaderboardWrapper";
import QuizQuestions from "@/components/quiz/common/QuizQuestions";
import QuizEmailModal from "@/components/quiz/common/QuizEmailModal";
import RecommendedQuizzes from "@/components/quiz/common/RecommendedQuizzes";
import Button from "@/components/common/PrimaryButton";

interface QuizResultsPageContentProps {
  results: QuizResult;
}

const QuizResultsPageContent: FC<QuizResultsPageContentProps> = ({
  results,
}) => {
  const { classes } = useStyles({ quizType: results.quiz.quizType });
  const router = useRouter();
  const user = useAppSelector((state) => state.user.user);
  const { width, height } = useWindowSize();
  const [storedResults, setStoredResults] = useLocalStorage("quizResults", []);
  const isQuestionAnswer = results.quiz.quizType === QuizType.QuestionAnswer;
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const isBusiness = results.isBusiness;

  useEffect(() => {
    if (storedResults && storedResults.length !== 0 && user) {
      const reqBody = {
        userId: user.id,
        quizResults: storedResults,
      };

      const sendQuizResults = async () => {
        try {
          await axios.patch(
            `${process.env.NEXT_PUBLIC_API_URL}/quiz/results`,
            reqBody,
            {
              withCredentials: true,
            }
          );
          router.refresh();
        } catch (error) {
          console.error("Error posting quiz results:", error);
        }
      };

      sendQuizResults();
      setStoredResults([]);
    }
  }, [storedResults, setStoredResults, user]);

  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    main.scrollTo(0, 0);
  }, []);

  return (
    <>
      {!isBusiness && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={1000}
        />
      )}
      <div className={classes.container}>
        <div className={classes.pillarAdLeft} />
        <div className={classes.content}>
          <div className={classes.contentInner}>
            {!isMobile && !isBusiness && (
              <MonumetricAd
                variant="header-in-screen"
                className={classes.inScreenAd}
              />
            )}

            <h1 className={classes.title}>{results.quiz.title}</h1>
            <h2 className={classes.yourResult}>Your Result:</h2>
            <h2 className={classes.resultValue}>{results.title}</h2>
            {isBusiness ? <Divider /> : <QuizResultHeader {...results} />}
            <div className={classes.resultDetails}>
              <div className={classes.thumbnailContainer}>
                <QuizResultThumbnail
                  title={results.title}
                  description={results.description}
                  thumbnail={results.relatedImageUrl}
                  quizType={results.quiz.quizType}
                  spotifyUrl={results.relatedSpotifyEmbedUrl}
                  isBusiness={isBusiness}
                  productPageUrl={results.productPageUrl}
                />
              </div>
              <div className={classes.descriptionContainer}>
                <div>
                  <p className={classes.description}>
                    {results.quiz.quizType === QuizType.QuestionAnswer
                      ? `${getScorePercentage(results.description).toFixed(
                          0
                        )}% correct (${results.description})`
                      : results.description}
                  </p>
                  {results.quiz.quizType === QuizType.QuestionAnswer && (
                    <>
                      {results.timeTaken !== null && (
                        <p className={classes.timeTaken}>
                          Time Taken: {formatTime(results.timeTaken)}
                        </p>
                      )}
                      <h2 className={classes.answersTitle}>
                        Scroll down to see answers
                      </h2>
                    </>
                  )}
                </div>
                <Watermark className={classes.laptopWatermark} />
              </div>
            </div>
            {results.quiz.quizType === QuizType.QuestionAnswer && (
              <h2 className={classes.answersTitleLaptop}>
                Scroll down to see answers
              </h2>
            )}
            {isBusiness && (
              <a href={results.productPageUrl}>
                <Button size="large" color="secondary">
                  Continue
                </Button>
              </a>
            )}
            <QuizResultControls results={results} />
            {!isBusiness && (
              <div className={classes.leaderboardWrapper}>
                <LeaderboardWrapper
                  quiz={results.quiz}
                  maxLeaderboardItems={10}
                />
              </div>
            )}
            {results.quiz.quizType === QuizType.QuestionAnswer && (
              <>
                <h2 className={classes.answersHeading}>Answers</h2>
                <div className={classes.answersContainer}>
                  <QuizQuestions
                    questions={results.quiz.questions}
                    answers={results.answers}
                    mode={isQuestionAnswer ? "result" : "quiz"}
                    disableInteractivity
                    quizType={results.quiz.quizType}
                    slug={results.quiz.slug}
                    isBusiness={results.quiz.isBusiness}
                  />
                </div>
              </>
            )}
            <RecommendedQuizzes quizzes={results.quiz.recommendedQuizzes} />
          </div>
        </div>
        {!isBusiness && (
          <div className={classes.pillarAdRight}>
            <MonumetricAd variant="pillar-right" />
          </div>
        )}

        {!isBusiness && (
          <QuizEmailModal
            quiz={results.quiz}
            resultUUID={results.uuid}
            delay={10000}
          />
        )}
      </div>
    </>
  );
};

export default QuizResultsPageContent;
